/**
 * Intenção de "especialidade gastronómica local" (módulo puro).
 *
 * Liga o foco vinculativo `sweets` quando o ÚNICO objetivo do pedido é
 * provar/comer/comprar doçaria ou uma especialidade concreta — sem depender da
 * palavra "só". Pedidos mistos continuam mistos; perguntas de recomendação
 * continuam a ser Descoberta.
 */

import { catalogSpecialtyForTerm, foldSpecialty } from "./local-specialty-catalog";

export type LocalSpecialtyIntent = {
  /** Foco exclusivo aplicável ao motor Premium. */
  binding: boolean;
  focus?: "sweets";
  /** Outro interesse explícito além da especialidade. */
  mixed: boolean;
  otherInterests: string[];
  /** Produto concreto pedido ("Ovos Moles", "Pastéis de Belém"). */
  targetProduct?: string;
  /** Estabelecimento nomeado ("Natário"). */
  targetVenue?: string;
  /** Pergunta de recomendação → Descoberta, nunca itinerário. */
  recommendation: boolean;
};

const CONSUMPTION_VERB =
  /(?:comer|provar|degustar|experimentar|comprar|saborear|petiscar|eat|taste|try|buy|purchase|probar|tomar|manger|gouter|goûter|deguster|déguster|acheter|mangiare|assaggiare|provare|comprare|essen|probieren|kaufen)/iu;

const SWEETS_TARGET =
  /(?:doces?\s+(?:regionais?|t[ií]picos?|tradicionais?|conventuais?)|do[cç]aria|pastelarias?|confeitarias?|sobremesas?\s+t[ií]picas?|traditional\s+sweets?|regional\s+sweets?|local\s+sweets?|pastry\s+shops?|patisserie|dolci\s+(?:tipici|tradizionali)|pasteles?\s+t[ií]picos?|dulces?\s+t[ií]picos?|s[uü][sß]igkeiten|geb[aä]ck)/iu;

const OTHER_INTERESTS: Array<{ id: string; re: RegExp }> = [
  { id: "cultural", re: /(?:cultura|cultural|hist[oó]ri[ac]|culture|heritage|patrimonio|patrim[oó]nio)/iu },
  { id: "museum", re: /(?:museus?|museums?|museos?|mus[eé]es?)/iu },
  { id: "monument", re: /(?:monumentos?|monuments?|castles?|pal[aá]cios?)/iu },
  { id: "church", re: /(?:igrejas?|churches?|catedrais?|cathedrals?|mosteiros?)/iu },
  { id: "viewpoint", re: /(?:miradour\w+|viewpoints?|miradores?)/iu },
  { id: "beach", re: /(?:praias?|beaches?|playas?)/iu },
  { id: "nature", re: /(?:natureza|nature|trilhos?|hiking|serras?|parques?\s+naturais?)/iu },
  { id: "shopping", re: /(?:compras|shopping|lojas?)/iu },
  { id: "nightlife", re: /(?:vida\s+noturna|nightlife|bares?|discotecas?)/iu },
];

const RECOMMENDATION =
  /(?:qual\s+(?:é|e)\s+o\s+melhor|quais?\s+(?:são|sao)\s+os\s+melhores|melhor\s+doce|o\s+que\s+devo\s+provar|onde\s+(?:posso\s+)?compr\w+|onde\s+(?:posso\s+)?prov\w+|recomend\w+|sugest\w+|what(?:'s| is)\s+the\s+best|where\s+can\s+i\s+(?:buy|try)|best\s+traditional)/iu;

/** "bolas de Berlim do Natário", "pastéis da Confeitaria X". */
const VENUE_OF =
  /(?:d[oa]s?\s+|from\s+|at\s+)([A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][\p{L}'.-]*(?:\s+[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][\p{L}'.-]*)*)/u;

export function detectLocalSpecialtyIntent(
  text: string,
  countryCode?: string | null,
): LocalSpecialtyIntent {
  const raw = (text ?? "").trim();
  const base: LocalSpecialtyIntent = {
    binding: false,
    mixed: false,
    otherInterests: [],
    recommendation: false,
  };
  if (!raw) return base;

  const rawCatalogHit = catalogSpecialtyForTerm(raw, countryCode);
  const catalogHit = rawCatalogHit?.focus === "sweets" ? rawCatalogHit : null;
  const hasSweetsTarget = SWEETS_TARGET.test(raw) || !!catalogHit;
  const recommendation = RECOMMENDATION.test(raw);
  const otherInterests = OTHER_INTERESTS.filter(({ re }) => re.test(raw)).map(({ id }) => id);

  const targetProduct = catalogHit?.product;
  let targetVenue: string | undefined;
  if (catalogHit?.specificTarget && catalogHit.venues.length) {
    targetVenue = catalogHit.venues[0]?.venueName;
  } else if (catalogHit?.specificTarget) {
    targetVenue = catalogHit.product;
  } else {
    const m = raw.match(VENUE_OF);
    const candidate = m?.[1]?.trim();
    if (candidate && hasSweetsTarget && !isPlaceWord(candidate)) targetVenue = candidate;
  }

  if (!hasSweetsTarget) return { ...base, otherInterests, recommendation };

  const wantsConsumption = CONSUMPTION_VERB.test(raw) || !!catalogHit;
  const mixed = otherInterests.length > 0;

  return {
    binding: wantsConsumption && !mixed && !recommendation,
    focus: wantsConsumption && !mixed && !recommendation ? "sweets" : undefined,
    mixed,
    otherInterests,
    targetProduct,
    targetVenue,
    recommendation,
  };
}

/** Palavras que são lugares, não estabelecimentos ("do Porto", "de Viana"). */
const PLACE_WORDS = new Set([
  "viana",
  "viana do castelo",
  "porto",
  "lisboa",
  "aveiro",
  "sintra",
  "belem",
  "portugal",
  "cidade",
  "regiao",
]);

function isPlaceWord(value: string): boolean {
  return PLACE_WORDS.has(foldSpecialty(value));
}
